/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

define([
	'../../../lib/@waca/dashboard-common/dist/core/Collection',
	'./SynchronizeDataEntry',
	'./SynchronizeDataHelper',
	'./SynchContextHelper',
	'./SynchJoinTable',
	'../../../filters/pagecontext/PageContextEntry',
	'../../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'../../../features/dashboard/queryService/QueryServiceUtils',
	'underscore'
], function(
	Collection,
	SynchronizeDataEntry,
	SynchronizeDataHelper,
	SynchContextHelper,
	SynchJoinTable,
	PageContextEntry,
	UniqueId,
	QueryServiceUtils,
	_
) {

	/**
	 * The Synchronize Model implements the boardModelExtension defined in synchronizeData.json
	 * It is used to persist information about mapped colours etc.
	 */
	var SynchronizeDataModel = Collection.extend({
		modelClass: SynchronizeDataEntry,

		init: function({ boardModel, dashboard }) {
			SynchronizeDataModel.inherited('init', this, arguments);
			this.boardModel = boardModel;
			this.dashboard = dashboard;
			this.logger = this.dashboard.getGlassCoreSvc('.Logger');
			this.modules = {};
			this.items = {};
			this.loadValuesPromises = {};
			this.synchronizeDataEntries = {};
			this.filterDockItems = {};
			this.jointedTablesBrushData = {};
			this.widgets = {};
			this.eventListener = this.boardModel.on('change:pageContext', this._onChangePageContext, this);
			this.eventListenerRemoveLayouts = this.boardModel.on('removeLayouts', this._onRemoveWidget, this);
			this.dashboard.on('brushing:undoRedo', this._onUndoRedo, this);
			this.dashboard.on('filterIndicator:removeEntry', this._onRemoveFilterIndicatorEntry, this);
			this.dashboard.on('widget:renderComplete', this._onRenderComplete, this);
		},

		destroy: function() {
			SynchronizeDataModel.inherited('destroy', this, arguments);
			if (this.eventListener) {
				this.eventListener.off();
				this.eventListener = null;
			}

			if (this.eventListenerRemoveLayouts) {
				this.eventListenerRemoveLayouts.off();
				this.eventListenerRemoveLayouts = null;
			}

			this.widgets = {};
			this.dashboard.off('brushing:undoRedo', this._onUndoRedo, this);
			this.dashboard.off('filterIndicator:removeEntry', this._onRemoveFilterIndicatorEntry, this);
			this.dashboard.off('widget:renderComplete', this._onRenderComplete, this);
		},

		/**
		* Loads the page context when first open the dashboard that has visualization widgets
		*
		* @param {object} pageContextAPI - the page context API
		* @param {object} visAPI - the viz API
		*
		* @return {object} A promise object
		*/
		loadBrushedContext: function(pageContextAPI, visualization, visAPI) {
			if (!pageContextAPI || !visAPI) {
				return Promise.resolve();
			}

			let dataItems = visualization.getSlots().getDataItemList();
			if (!dataItems || dataItems.length === 0) {
				return Promise.resolve();
			}

			let projectedItems = SynchContextHelper.createSynchProjectedItems(visAPI.getModule(), dataItems.map( dataItem => {
				return {
					getItemId: () => dataItem.getColumnId(),
					getLabel: () => dataItem.getLabel()
				};
			}));
			let pageContextMatchParams = {
				origin: 'visualization',
				scope: visAPI.getScope(),
				eventGroupId: visAPI.getEventGroupId()
			};

			let pageContextItems = pageContextAPI.getPageContextItems(pageContextMatchParams);
			if (pageContextItems.length === 0) {
				return Promise.resolve();
			}

			let promises = [];
			pageContextItems.forEach(pageContextItem => {
				let spec = pageContextItem.getPageContextSpec();
				let options = {
					module: visAPI.getModule(),
					itemIds: _.pluck(spec.hierarchies, 'hierarchyUniqueName'),
					data: spec.tupleSet
				};
				promises.push(this._synchJointedTables(projectedItems, options));
			});

			return Promise.all(promises);
		},

		/**
		* Retrieve and return the found page context
		* This function is ensuring projected data items in joint tables have correct brushed values in vis
		*
		* @param {string} dataSourceId - the data source id
		* @param {object} pageContextAPI - the page context API
		* @param {string} itemId - the data item to search for a corresponding joined table tuple value
		* @param {string} pageContextMatchParams - the page context scope to search for page context item
		*
		* @return {object} page context entry API object if one found else return undefined
		*/
		getPageContextItem: function(dataSourceId, pageContextAPI, itemId, pageContextMatchParams) {
			var pageContextItem = pageContextAPI.getPageContextItem(_.extend({
				itemId: itemId
			}, pageContextMatchParams));

			if (pageContextItem) {
				//Found it so return
				return pageContextItem;
			}

			let pageContextItems = pageContextAPI.getPageContextItems({
				eventGroupId: pageContextMatchParams.eventGroupId,
				origin: pageContextMatchParams.origin,
				scope: pageContextMatchParams.scope
			});

			let synchColumn;
			let synchModule = this.jointedTablesBrushData[dataSourceId];
			if (synchModule) {
				synchColumn = synchModule.getColumn(itemId);
			}

			if (!synchColumn || pageContextItems.length === 0) {
				return undefined;
			}

			for(let idx = 0; idx < pageContextItems.length; idx++) {
				let pageContextItem = pageContextItems[idx];
				let result = this._findJoinedTableTupleValue(pageContextItem, synchColumn);
				if (result) {
					//Clone the spec so not to modify the original page context entry model
					var newSpec = JSON.stringify(pageContextItem.getPageContextSpec());
					newSpec = JSON.parse(newSpec);
					newSpec.hierarchies = [{ hierarchyUniqueName: itemId }];
					newSpec.tupleSet = result.value;

					return new PageContextEntry(newSpec).getAPI();
				}
			}
		},

		/**
		* Find and return a matched tuple value if tables are joined
		* @private
		*
		* @param {object} pageContextItem - the pageContextItem to find a jointed table tuple value
		* @param {object} synchColumn - the column to look up for the jointed table tuple value
		*
		* @return and object containing the result if one found else return undefined
		*/
		_findJoinedTableTupleValue: function(pageContextItem, synchColumn) {
			let spec = pageContextItem.getPageContextSpec();
			let itemIds = _.pluck(spec.hierarchies, 'hierarchyUniqueName');
			let data = this._isArrayOfArray(spec.tupleSet) ? spec.tupleSet : [spec.tupleSet];

			let matchedItemId;
			let matchedTuples;

			for(let idx = 0; idx < itemIds.length; idx++) {
				data.forEach(tupleSet => {
					tupleSet.forEach(function(tuple, currentIdx) {
						if (idx === currentIdx) {
							matchedItemId = itemIds[idx];
							let foundTuple = synchColumn.getJointTupleValue(itemIds[idx], tuple);
							if (foundTuple) {
								if (!matchedTuples) {
									matchedTuples = [];
								}
								//Array array of values
								matchedTuples.push([foundTuple]);
							}
						}
					});
				});

				if (matchedTuples) {
					return {
						itemId: matchedItemId,
						value: matchedTuples
					};
				}
			}

		},

		/**
		* Call to handle a brushing selection to synchronize data across data sources
		*
		* @param {object} selection - the selected brushing context
		* @param {object} options - the selected brushing options
		* Example of brushing spec
		* {
		*  "origin": "visualization",
		* "eventSourceId": "model0000015fef997299_00000000",
		* "sourceId": "model0000015fef992bd7_00000001",
		* "scope": "page1",
		* "eventGroupId": "page1:1",
		* "hierarchies": [
		*   {}
		* ],
		* "hierarchyUniqueNames": [
		*   null
		* ]
		*}
		* @return {object} A resolved promise after brushing is handled to synchronize the data across data sources
		*/
		handleBrushingSelection: function(selection = {}, options = {}) {
			const synchOptions = options.synchOptions || {};
			return this._doBrushingSelection(selection, options)
				.then(results => {
					if (results) {
						results.arguments = {
							command: selection.command,
							pending: selection.pending
						};

						let append = selection.slotsToClear && selection.slotsToClear.length === 0;
						selection.synchDataId = UniqueId.get('');

						(results.data || []).map(item => {
							item.synchDataId = selection.synchDataId;
							item.append = append;
							item.getNetSlotsToClear = SynchContextHelper.getNetSlotsToClearBrushing;
							return item;
						});

						this.dashboard.triggerDashboardEvent('synchronizeData:applyBrushing', results);

						//Is this a non-projected items clear action?
						const newSelectionIsEmpty = synchOptions.newSelectionIsEmpty(selection.tuple);
						if (newSelectionIsEmpty && selection.slotsToClear.length > 0) {
							selection.slotsToClear = SynchContextHelper.getNetSlotsToClearBrushing({
								pageContext: synchOptions.pageContext,
								selector: {
									origin: 'visualization',
									scope:  selection.brushingSpec.scope,
									eventGroupId: selection.brushingSpec.eventGroupId,
									sourceId: selection.brushingSpec.sourceId
								},
								sourceModule: results.brushSource.getModule(),
								targetModule: results.brushSource.getModule(),
								dataSlots: selection.slotsToClear,
								itemIds: selection.itemIds,
								tableNames: results.brushSource.getTableRef(),
								newSectionFromSource: true,
								newSelectionIsEmpty: true
							});
						}
					}
					return results;
				})
				.finally(() => {
					this._resetData();
				});
		},


		_resetData: function() {
			this.modules = {};
			this.items = {};
			this.loadValuesPromises = {};
			this.synchronizeDataEntries = {};
		},

		_doBrushingSelection: function(selection = {}, options = {}) {
			//Synchronizaton of data does not support right mouse click (which is for pending = true)
			if (selection.pending) {
				return Promise.resolve();
			}

			var brushingSpec = selection.brushingSpec;
			this._resetBrushing(options);
			var context = {
				scope: {
					origin: 'visualization',
					scope:  brushingSpec.scope,
					eventGroupId: brushingSpec.eventGroupId,
					eventSourceId: brushingSpec.eventSourceId
				},
				itemIds: selection.itemIds,
				tuple: selection.tuple
			};

			return this._getModulesWithinSameEventGroup(brushingSpec.sourceId, brushingSpec.eventGroupId, brushingSpec.eventSourceId)
				.then((brushingEntries) => {
					if (brushingEntries) {
						const results = this._synchBrushingSelection(context, options, brushingEntries);
						if (results) {
							results.scope = context.scope;
						}
						brushingSpec.tableRef = brushingEntries.brushSource.getTableRef();
						return results;
					}
				});
		},

		_getNetPageContextItems: function(scope, eventGroupId, origin, synchDataId) {
			var paramsForPageContext = {
				scope: scope,
				eventGroupId: eventGroupId,
				origin: origin || 'visualization'
			};

			if (synchDataId) {
				paramsForPageContext.synchDataId = synchDataId;
			}

			return this.pageContext ? this.pageContext.getNetPageContextItems(paramsForPageContext) : [];
		},

		/**
		* Call to synchronize data for all data sources within same event group id
		*
		* @param {string} sourceId - the current selected souce id
		* @param {string} eventGroupId - the current selected event group id
		* @param {string} eventSourceId - where the event originating from
		*
		* @param {object} return all data source modules base on the specified values of sourceId and eventGroupId
		*/
		_getModulesWithinSameEventGroup: function(sourceId, eventGroupId, eventSourceId) {
			var dataViewModelMap = {};
			var dataViewModels = function(widget) {
				//return the fist on for now
				var models;
				var data = widget ? widget.data : null;
				if (data) {
					models = data.dataViews;
				}
				return models;
			};

			const dashboardInternal = this.dashboard.getFeature('internal');
			var sources = [];
			const boardModel = dashboardInternal.getBoardModel();
			this.pageContext = this.pageContext || boardModel.get('pageContext').getAPI();
			var eventGroups = boardModel.get('eventGroups');
			var eventGroup = eventGroups.get(eventGroupId);

			if (eventGroup) {
				var widgetIds = eventGroup.get('widgetIds');
				widgetIds.forEach(widgetId => {
					var widget = boardModel.findWidgetById(widgetId);
					var models = dataViewModels(widget);
					if (models) {
						models.forEach(model => {
							if (model.modelRef) {
								if (sources.indexOf(model.modelRef) === -1) {
									sources.push(model.modelRef);
								}
								if (!dataViewModelMap[model.modelRef]) {
									dataViewModelMap[model.modelRef] = [];
								}
								if (model.dataItems) {
									dataViewModelMap[model.modelRef].push({ widgetId: widgetId, projectedItems: model.dataItems.map(dataItemModel => {
										return {
											getItemId: () => dataItemModel.itemId,
											getLabel: () => dataItemModel.itemLabel
										};
									}) });
								}
							}
						});
					}
				});
			}


			const dataSources = this.dashboard.getFeature('dataSources.deprecated');
			var promises = [];
			_.each(sources, function(id) {
				if (this.modules[id]) {
					promises.push(Promise.resolve(this.modules[id]));
				} else {
					// datasources.depreacted is not a core feature. Module is only required for synchronized data, unfortunately
					// the current brushing is tightly coupled with synch'd data.
					// @todo synchronized data should be supported as a query modifier, instead of overcomplicating the page context
					promises.push(dataSources ? dataSources.getModule(id) : Promise.resolve(null));
				}
			}, this);

			return Promise.all(promises)
				.then(function(results) {
					_.each(results, function(module) {
						// module is not required when synchronized data is not supported
						if(module && !(module.getSourceId() in this.modules)) {
							this.modules[module.getSourceId()] = module; //add to cache
						}
					}, this);
				}.bind(this))
				.then(() => {
					return this._createSynchBrushEntries(eventGroupId, eventSourceId, dataViewModelMap);
				});
		},

		_createBrushEntry: function(eventGroupId, dataViewModelMap, eventSourceId, callback) {
			let newBrushEntry;

			for (var modelRef in dataViewModelMap) {
				var dataViewModel = dataViewModelMap[modelRef];
				if (!dataViewModel.length) {
					continue;
				}
				_.find(dataViewModel, dataView => {
					if (dataView.projectedItems.length) {
						var module = this.modules[modelRef];
						if (!eventSourceId || (dataView.widgetId === eventSourceId)) {
							newBrushEntry = callback(modelRef, eventGroupId, module, dataView.projectedItems, eventSourceId);
							return true;
						}
					}
					return false;
				});

				if (newBrushEntry) {
					break;
				}
			}

			return newBrushEntry;
		},

		_createSynchBrushEntries: function(eventGroupId, eventSourceId, dataViewModelMap) {
			var brushEntries = [];

			var instantianNewBrushEntry =  function(modelRef, eventGroupId, module, projectedItem, widgetId) {
				let fromObject = {
					getSourceId: () => {
						return modelRef;
					},
					getEventGroupId: () => {
						return eventGroupId;
					},
					getEventSourceId: () => {
						return projectedItem.widgetId;
					}
				};
				return SynchContextHelper.createNewSynchContext({
					fromObject: fromObject,
					module: module,
					projectedItems: projectedItem,
					widgetId: widgetId
				});
			};


			/**
			* dataViewModelMap = {
			* modelRef: [{
			*	widgetId: widgetId,
			*	projectedItems: [...]
			* }
			*
			*/

			let synchSourceContext = this._createBrushEntry(eventGroupId, dataViewModelMap, eventSourceId, instantianNewBrushEntry);

			if (!synchSourceContext) {
				return synchSourceContext;
			}

			for (var modelRef in dataViewModelMap) {
				var dataViewModel = dataViewModelMap[modelRef];
				if (!dataViewModel.length) {
					continue;
				}
				dataViewModel.forEach(dataView => {
					if (dataView.projectedItems.length && dataView.widgetId !== eventSourceId) {
						var module = this.modules[modelRef];
						var newBrushEntry = instantianNewBrushEntry(modelRef, eventGroupId, module, dataView.projectedItems, dataView.widgetId);
						if (newBrushEntry) {
							if (SynchContextHelper.haveJoinedTables(synchSourceContext, newBrushEntry)) {
								synchSourceContext.mergeProjectedItems(newBrushEntry);
								synchSourceContext.addWidgetId(dataView.widgetId);
							} else {
								var existedEntry = _.find(brushEntries, entry => {
									return SynchContextHelper.haveJoinedTables(entry, newBrushEntry);
								});

								if (existedEntry) {
									existedEntry.mergeProjectedItems(newBrushEntry);
									existedEntry.addWidgetId(dataView.widgetId);
								} else {
									brushEntries.push(newBrushEntry);
								}
							}
						}
					}
				});
			}

			return { brushSource: synchSourceContext, entries: brushEntries };
		},

		/**
		* Synchronize joined table tuple values match by column names in jointed tables
		*
		* @param {array} projectedItems - array of viz projected items to synch tuple values
		* @param {object} options - options to synch joined table values
		*
		* @return {object} A promise object
		*/
		_synchJointedTables: function(projectedItems, options = {}) {
			let module = options.module;
			let itemIds = options.itemIds;

			if (!projectedItems || projectedItems.length === 0 || !itemIds || itemIds.length == 0 || !options.data || options.data.length === 0) {
				return Promise.resolve();
			}

			var promises = [];
			let isArrayOfArray = this._isArrayOfArray(options.data);
			let data = isArrayOfArray ? options.data : [options.data];

			itemIds.forEach(function(itemId, idx) {
				let handlers = {
					loadValues: this._loadValues.bind(this)
				};
				let synchModule = this.jointedTablesBrushData[module.getSourceId()] = this.jointedTablesBrushData[module.getSourceId()] || new SynchJoinTable(module, { handlers: handlers });
				let synchColumn = synchModule.getColumn(itemId);

				if (synchColumn) {
					projectedItems.forEach(projectedItem => {
						if (synchColumn.getItemId() !== projectedItem.getItemId() && synchColumn.matchedColumnByName(projectedItem.getItemId())) {
							let synchToFindMatchedValue = synchModule.getColumn(projectedItem.getItemId());
							data.forEach(tupleSet => {
								tupleSet.forEach(function(tuple, currentIdx) {
									if (idx === currentIdx) {
										promises.push(synchToFindMatchedValue.matchJointedTableValue(synchColumn, tuple));
									}
								});
							});
						}
					});
				}
			}.bind(this));

			return Promise.all(promises);
		},

		_clearBrushing: function(tuple) {
			return (!tuple || !_.flatten(tuple).length);
		},

		_synchClearBrushing: function(context = {}, options = {}, brushingEntries = {}) {
			if (!brushingEntries.entries || brushingEntries.entries.length === 0 || !this._clearBrushing(context.tuple)) {
				return null;
			}

			let results = [];
			const cloneOptions = SynchronizeDataHelper.clone(options);
			const payloadData = cloneOptions.payloadData = cloneOptions.payloadData || {};
			payloadData.brushedSourceModule = brushingEntries.brushSource.getModule();
			payloadData.ignorePageContextChanged = true;
			const entries = [brushingEntries.brushSource].concat(brushingEntries.entries);

			entries.forEach(entry => {
				results.push({
					sourceId: entry.getSourceId(),
					tableRef: entry.getTableRef(),
					itemIds:  context.itemIds,
					sourceModule: brushingEntries.brushSource.getModule(),
					tuple: context.tuple,
					options: cloneOptions,
					ignoreSynchBrushData: this._getIgnoreSynchBrushDataWidgetIdList(entry.getWidgetIds())
				});
			});
			return (results.length > 0) ? { data: results, brushSource: brushingEntries.brushSource } : null;
		},

		/**
		* Internal handling of brushing to synchronize the data
		*
		* @param {array} itemIds - array of selected data items (e.g. [Product line, Order method])
		* @param {array} tuple - the selected tuple values (e.g. [Camping Equipment, Fax])
		* @param {object} options - the selected brushing options
		* @param {object} brushingEntries - the brushing entries to aply brushing
		*
		* @return {object} an object contain the synchronize brushing data
		*/
		_synchBrushingSelection: function(context, options = {}, brushingEntries = {}) {
			if (!brushingEntries.entries || brushingEntries.entries.length === 0) {
				return null;
			}

			if (this._clearBrushing(context.tuple)) {
				return this._synchClearBrushing(context, options, brushingEntries);
			}

			var brushigData = this._constructBrushingData(context, brushingEntries);

			const results = [];
			_.each(brushigData.entries, function(entry) {
				results.push(this._synchEntry(entry.pageContextEntry, { source: brushigData.source, target: entry.targetSynchDataEntry, brushing: true, scope: context.scope }));
			}, this);

			if (results.length > 0) {
				var synchBrushingData;
				var cloneOptions = SynchronizeDataHelper.clone(options);
				var payloadData = cloneOptions.payloadData = cloneOptions.payloadData || {};
				payloadData.brushedSourceModule = brushingEntries.brushSource.getModule();
				payloadData.ignorePageContextChanged = true;
				results.forEach((result, index) => {
					if (result) {
						if (!synchBrushingData) {
							synchBrushingData = [];
						}

						var entry = brushigData.entries[index].pageContextEntry;
						var targetSynchDataEntry = brushigData.entries[index].targetSynchDataEntry;
						var isArrayOfArray = brushigData.entries[index].isArrayOfArray;
						var tupleSet = isArrayOfArray ? entry.tupleSet : (entry.tupleSet.length > 0 ? entry.tupleSet[0] : []);
						let value = {
							sourceId: targetSynchDataEntry.getSourceId(),
							tableRef: targetSynchDataEntry.getTableRef(),
							itemIds:  _.pluck(entry.hierarchies, 'hierarchyUniqueName'),
							sourceModule: brushingEntries.brushSource.getModule(),
							tuple: tupleSet,
							options: cloneOptions,
							ignoreSynchBrushData: this._getIgnoreSynchBrushDataWidgetIdList(targetSynchDataEntry.getWidgetIds())
						};

						synchBrushingData.push(value);
					}
				}, this);

				return { brushSource: brushingEntries.brushSource, data: synchBrushingData };
			}
		},

		/**
		* Return an array of widget ids that should ignore synchronize brushed data
		*
		* @param {array} widgetIds - array of widget ids to process
		*
		* @return {array} List of widget ids that must ignore synchronize brushed dataś
		*/
		_getIgnoreSynchBrushDataWidgetIdList: function(widgetIds = []) {
			let edgeSelectionIsTrue = [];
			let edgeSelectionIsFalse = [];

			widgetIds.forEach(id => {
				let widget = this.widgets[id];
				if (widget) {
					if (widget.getEdgeSelection()) {
						edgeSelectionIsTrue.push(widget.getId());
					} else {
						edgeSelectionIsFalse.push(widget.getId());
					}
				}
			});

			//Non edge selection brushing type take precedence over edge selection type.
			//Currenty summary, table, grid have edgeSection type equals to true, the rest have this flag equal to false
			//During synchronize brushing, edge selection type is ignoring synch brush event to avoid creating edge brush event if there is at least one non-edge selection vis
			return (edgeSelectionIsFalse.length) ? edgeSelectionIsTrue : [];
		},

		/**
		* Construct brushing data to synchronize across data sources
		*
		* @param {array} itemIds - array of selected data items (e.g. [Product line, Order method])
		* @param {array} tuple - the selected tuple values (e.g. [Camping Equipment, Fax])
		*
		* @param {object} return an object containing the synchronize brushing data
		*/
		_constructBrushingData: function(context = {}, brushingData = {}) {
			var dataEntry = this._getSynchDataEntry(brushingData.brushSource.getSourceId(), context.scope, brushingData.brushSource.getTableRef());
			brushingData.brushSource.setSynchDataEntry(dataEntry);

			var targets = this._getTargetBrushingEntries(brushingData.entries, context.scope);

			return {
				source: brushingData.brushSource.getAPI(),
				entries: this._constructPageContextEntries(context.itemIds, context.tuple, brushingData.brushSource, targets)
			};
		},

		/**
		* Construct page context entries for brushing
		*
		* @param {array} itemIds - array of selected data items (e.g. [Product line, Order method])
		* @param {array} tuple - the selected tuple values (e.g. [Camping Equipment, Fax])
		* @param {object} source - the source brushing
		* @param {object} targets - an object containing the set of target modules
		*
		* @param {array} return an array of page context entries
		*/
		_constructPageContextEntries: function(itemIds, tuple, source, targets) {
			var entries = [];
			_.each(targets, (target) => {
				var isArrayOfArray = this._isArrayOfArray(tuple);
				var clonedTuple = SynchronizeDataHelper.clone(tuple);
				var pageContextEntry = {
					hierarchies: this._getHierarchies(itemIds),
					tupleSet: isArrayOfArray ? clonedTuple : [clonedTuple],
					sourceId: source.getSourceId()
				};
				entries.push( { pageContextEntry: pageContextEntry, targetSynchDataEntry: target, isArrayOfArray: isArrayOfArray });
			});
			return entries;
		},

		_getHierarchies: function(itemIds) {
			if ( Array.isArray(itemIds) ) {
				return _.map(itemIds, function(itemId) {
					return { hierarchyUniqueName: itemId };
				});
			} else if ( _.isString(itemIds) ){
				return [{ hierarchyUniqueName: itemIds }];
			} else {
				return [];
			}
		},

		_isArrayOfArray: function(tuple) {
			return _.isArray(tuple) && tuple.length > 0 && _.isArray(tuple[0]);
		},

		synchTupleSet: function(module, itemIds, tupleSet) {
			var synched = false;
			var cloneTupleSet = SynchronizeDataHelper.clone(tupleSet);

			_.each(cloneTupleSet, function(tuple) {
				_.each(tuple, function(value, index) {
					var itemId = itemIds[index].hierarchyUniqueName;
					var values = this._getValues(module.getSourceId(), itemId) || [];
					if (SynchronizeDataHelper.matchItemByValue(values, value)) {
						synched = true;
					}
				}, this);
			}, this);

			return synched ? cloneTupleSet : tupleSet;
		},

		/**
		* @override
		*/
		toJSON: function() {
			//1. clone models
			var modelMap = {};

			this.each(function(model) {
				if(model.hasSynchronizeData()) {
					var spec = model.toJSON();
					var cloneModel = new SynchronizeDataEntry(spec);
					modelMap[SynchContextHelper.getKey(cloneModel.getSourceId(), cloneModel.getTableRef())] = cloneModel;
				}
			}.bind(this));

			//2. merge entries to remove duplicates
			_.each(modelMap, function(model) {
				SynchronizeDataHelper.mergeDuplicates(model.getSourceId(), model.getTableRef(), model.getSynchronizeItems(), modelMap);
			});

			//3. Remove ones not needed
			_.each(modelMap, function(model) {
				if (!model.hasSynchronizeData()) {
					delete modelMap[SynchContextHelper.getKey(model.getSourceId(), model.getTableRef())];
				}
			});

			return _.map(modelMap, function(model) {
				return model.toJSON();
			});
		},

		/**
		* Returns a boolean indicating whether the column name has been referenced in filter dock.
		* The column name is matched in all data sources and return true when there is a first matched column name in a data source
		*
		* @param {object} scopeToCheck - the scope contains the set of data sources to search for a match of column name
		* @param {object} column - the column whose name is being search to match
		*
		* @return {boolean} true if there is a match else return false
		*/
		_hasFilterDockItemMatchByColumnName: function(scopeToCheck, column) {
			var label = column.getLabel();
			var hasFilterDockItem = false;
			for (var sourceIdToCheck in scopeToCheck) {
				var filterDockItem = _.find(scopeToCheck[sourceIdToCheck], function(item) {
					return item.getItemName() === label;
				});

				if (filterDockItem) {
					hasFilterDockItem = true;
					break;
				}
			}

			return hasFilterDockItem;
		},

		/**
		* Add/remove a column from filter dock
		*
		* @param {object} item - the filter dock item being added/deleted from filter dock
		* @param {string} scope - the scope (global/current tab) where the item is being added/deleted
		* @param {boolean} isAdding - true when the item is being added

		*/
		onAddRemoveFilterItem: function(item, scope, isAdding) {
			if (item && scope) {
				var currentScope =  this.filterDockItems[scope] = this.filterDockItems[scope] || {};
				var currentSource = currentScope[item.getSourceId()] = currentScope[item.getSourceId()] || {};
				if (isAdding) {
					currentSource[item.getItemId()] = currentSource[item.getItemId()] || item;
				} else {
					delete currentSource[item.getItemId()];
				}
			}
		},

		/**
		* Internal api to handle the synchronization of data for the pageContextEntry
		*
		* @param {object} pageContextEntry - the page context entry to synchronize data
		* @param {object} options.source - the SynchronizeDataEntry to synchronize data from
		* @param {object} options.target - the SynchronizeDataEntry to synchronize data to
		*
		* @return {boolean} Return the synchronized data for the pageContextEntry
		*/
		_synchEntry: function(pageContextEntry, options = {}) {
			//clone here since removing collection items on the fly, done to not affect the iterating over collection process
			const results = [];
			const cloneHierarchies = SynchronizeDataHelper.clone(pageContextEntry.hierarchies);
			_.each(cloneHierarchies , function(hierarchy, index) {
				var context = {
					id: hierarchy.hierarchyUniqueName,
					tupleSet: pageContextEntry.tupleSet,
					conditions: pageContextEntry.conditions,
					index: index,
					source: options.source,
					target: options.target,
					scope: options.scope,
					origin: options.origin,
					brushing: options.brushing
				};
				results.push(this._synchItemUseValues(pageContextEntry.hierarchies, context));
			}, this);

			if (results.length > 0) {
				let applySynchronized = false;
				let synchedHierarchies = [];
				let synchedTupleSet = [];
				results.forEach((isSynchronized, hierarchyIdx) => {
					const result = SynchronizeDataHelper.isBoolean(isSynchronized) ? isSynchronized : false;
					if (result) {
						if (!applySynchronized) {
							applySynchronized = result;
						}
						synchedHierarchies.push(pageContextEntry.hierarchies[hierarchyIdx]);
						pageContextEntry.tupleSet.forEach((tuple, tupleSetIdx) => {
							let resultTuple;
							if (tupleSetIdx < synchedTupleSet.length) {
								resultTuple = synchedTupleSet[tupleSetIdx];
							} else {
								resultTuple = [];
								synchedTupleSet.push(resultTuple);
							}
							tuple.forEach((value, tupleIdx) => {
								if (hierarchyIdx === tupleIdx) {
									resultTuple.push(value);
								}
							});
						});
					}
				});

				pageContextEntry.hierarchies = synchedHierarchies;
				pageContextEntry.tupleSet = synchedTupleSet;
				if (applySynchronized) {
					pageContextEntry.sourceId = options.target.getSourceId();
				}
				return applySynchronized;
			}
			return false;
		},

		/**
		* Internal api to handle the synchronization of data for the hierarchies
		*
		* @param {object} hierarchies - the collection of hierarchies to synchronize data to
		* @param {string} options.id - the data item use value to synchronize data from
		* @param {object} options.tupleSet - the tupleSet to synchronize data to
		* @param {object} options.source - the source to synchronize data from
		* @param {object} options.target - the target to synchronize data to
		*
		* @return {boolean} the synchronized boolean flag indicating whether the hierarchies had been synchronized or not
		*/
		_synchItemUseValues: function(hierarchies, options) {
			options = options || {};
			var source = options.source;
			var target = options.target;

			var matchedColumn;
			var sourceModule = this.modules[source.getSourceId()];
			var targetModule = this.modules[target.getSourceId()];
			var synchSource = source.getSynchronizeItemId(options.id, target.getSourceId(), target.getTableRef());

			if (synchSource) {
				matchedColumn = targetModule.getMetadataColumn(synchSource.itemId);
			} else {
				var column = sourceModule.getMetadataColumn(options.id);
				if (column) {
					var context = {
						projectedItems: target.getProjectedItems()
					};
					matchedColumn = SynchronizeDataHelper.matchColumByName(sourceModule, targetModule, column, context);
				}
			}

			if (!matchedColumn) {
				return Promise.resolve(false);
			}

			this._synchConditions(options.conditions, matchedColumn);
			const isSynchronized =  options.conditions ?  true : this._synchByValues(targetModule, {
				sourceColumn: source.getModule().getMetadataColumn(options.id),
				targetColumn: matchedColumn,
				hierarchies: hierarchies,
				tupleSet: options.tupleSet,
				conditions: options.conditions,
				currentIndex: options.index,
				scope: options.scope,
				origin: options.origin,
				brushing: options.brushing
			});

			if (isSynchronized) {
				hierarchies[options.index].hierarchyUniqueName = matchedColumn.getId();
				if (!synchSource) {
					source.addSynchronizeItem(options.id, target.getSourceId(), matchedColumn.getId(), target.getTableRef());
				}
				target.addSynchronizeItem(matchedColumn.getId(), source.getSourceId(), options.id, source.getTableRef());
				this._addSynchronizeDataEntry(source);
				this._addSynchronizeDataEntry(target);
			}
			return isSynchronized;
		},

		/**
		* Internal api to handle the synchronization of data for the conditions
		*
		* @param {string} options.targetColumn - the target metadata column to synch data to
		* @param {array} options.conditions - the hierarchies to synch data to
		*
		* @return {object} A promise resolved to return the synchronized data for the conditions
		*/
		_synchConditions: function(conditions, targetColumn) {
			if (conditions && targetColumn) {
				_.each(conditions, (condition) => {
					var attributeUniqueNames = condition.attributeUniqueNames;
					if (_.isArray(attributeUniqueNames) && attributeUniqueNames.length > 0) {
						attributeUniqueNames[0] = targetColumn.getId();
					}
				});
			}
		},

		_emptyTupleSet: function(tupleSet) {
			return !(tupleSet && !!tupleSet.length && tupleSet[0] && !!tupleSet[0].length);
		},

		/**
		* Internal api to handle the synchronization of data for the hierarchies
		*
		* @param {object} targetModule - the target module to synch data to
		* @param {string} options.targetColumn - the target metadata column to synch data to
		* @param {array} options.hierarchies - the hierarchies to synch data to
		* @param {array} options.tupleSet - the tuple set to synch data to
		* @param {number} options.currentIndex - the current index to the hierarchies array
		*
		* @return {boolean} Return the synchronized data for the hierarchies and tupleSet
		*/
		_synchByValues: function(targetModule, options = {}) {
			const tupleSet = options.tupleSet;

			/*Note added January 17, 2019
			* ============================
			* Synchronize data between different tables had been wired off for other types of datasource such as package. Currently only flat file types for flat file types within modules are supported.
			* Synchronize data between Global/local filters are also wired off as well. Since for filters users can define any values to filter where these values may be not be in the data source, this is a non-issue here as well
			*
			* Thefore what is left here is synchronize between brushing which requires looking at the data in the data source.
			*/
			if (options.brushing && this._emptyTupleSet(tupleSet)) {
				return Promise.resolve(true);
			}

			const currentIndex = options.currentIndex;
			let isSynchronized = false;
			const cloneTupleSet = SynchronizeDataHelper.clone(tupleSet);

			const matchTupleSet = () => {
				cloneTupleSet.forEach((tuple, outerIndex) =>{
					tuple.forEach((value, index) => {
						if (tupleSet[outerIndex][index] && index === currentIndex) {
							if (SynchContextHelper.replaceUseValue(options.targetColumn, tupleSet[outerIndex][index])) {
								isSynchronized = true;
							} else {
								//Removing the item since don't match
								options.hierarchies.splice(index, 1);
								tupleSet[outerIndex].splice(index, 1);
							}
						}
					});
				});
			};

			matchTupleSet();
			return isSynchronized;
		},

		_addItemValues: function(sourceId, id, values) {
			if (!(sourceId in this.items)) {
				this.items[sourceId] = {};
			}
			var item = this.items[sourceId];
			item[id] = values;
		},

		_getModule: function(sourceId) {
			return this.modules[sourceId];
		},

		_loadValues: function(module, id) {
			var key = module.getSourceId() + id;
			var promise = this.loadValuesPromises[key];

			if (!promise) {
				promise = this.loadValuesPromises[key] = this._loadValuesForMetadataColumn(module, id);
			}

			return promise.then(function(values) {
				this._addItemValues(module.getSourceId(), id, values);
				return values;
			}.bind(this));
		},
		_getSynchDataEntry: function(sourceId, scope, tableRef) {
			var entry  = this.synchronizeDataEntries[SynchContextHelper.getKey(sourceId, tableRef)];
			if (!entry || !entry.inSameScope() || !entry.inSameTable(tableRef)) {
				var spec = {
					sourceId: sourceId,
					scope: scope.scope,
					eventGroupId: scope.eventGroupId,
					tableRef: tableRef
				};
				entry = new SynchronizeDataEntry(spec);
			}
			return entry;
		},

		_getTargetBrushingEntries: function(context, scope) {
			var entries = {};
			_.each(context, function(contextEntry) {
				var tableRef = contextEntry.getTableRef();
				var sourceId =contextEntry.getSourceId();
				var dataEntry  = this._getSynchDataEntry(sourceId, scope, tableRef);
				contextEntry.setSynchDataEntry(dataEntry);
				entries[SynchContextHelper.getKey(sourceId, tableRef)] = contextEntry.getAPI();
			}.bind(this));

			return entries;
		},

		_addSynchronizeDataEntry: function(fromContext) {
			let key = SynchContextHelper.getKey(fromContext.getSourceId(), fromContext.getTableRef());
			var entry = this.synchronizeDataEntries[key];
			if (entry) {
				entry.set({ synchronizeItems: fromContext.getSynchronizeItems() }, { silent: true } );

			} else {
				var spec = fromContext.toJSON();
				var newEntry = this.add(spec, { silent: true });
				this.synchronizeDataEntries[key] = newEntry;
			}
		},

		_loadValuesForMetadataColumn: function(module, id) {
			const queryHelper = this.dashboard.getFeature('QueryService');
			return queryHelper.getColumnValues(module.getSourceId(), [id], [], 'asc').then(values => {
				return QueryServiceUtils.extractListOfQueryResultData(values);
			});
		},

		_getValues: function(sourceId, itemId) {
			return (sourceId in this.items) ? this.items[sourceId][itemId] : null;
		},

		_getConditionValue: function(conditions) {
			var value = {};
			if(_.isArray(conditions) && !!conditions.length) {
				value.from = conditions[0].from[0];
				value.to = conditions[0].to[0];
				value.invert = conditions[0].invert;
			}
			return value;
		},

		/**
		* Calls to reset brushing context when first brushing occur
		*
		* @param {object} options - brushing options
		*/
		_resetBrushing: function(options) {
			options = options || {};
			var payloadData = options.payloadData || {};
			payloadData.isBrushed = true;
			this.undoRedo = [];
		},

		/**
		* Handle 'change:pageContext' event
		*
		* @param {object} event - the event object of the 'change:pageContext' event
		*/
		_onChangePageContext: function(event) {
			if (this._canProcessPageContextChange(event)) {
				var cloneData = SynchronizeDataHelper.clone(event.data);
				cloneData.ignoreUndoPageContextChanged = (this.undoRedo.length === 0) ? false : true;
				cloneData.ignoreRedoPageContextChanged = false;
				event.data = cloneData;
				this.undoRedo.push(event);

				for(var idx = 0; idx < this.undoRedo.length - 1; idx++) {
					this.undoRedo[idx].data.ignoreRedoPageContextChanged = true;
				}
			}
		},

		/**
		* Handle 'brushing:undoRedo' event
		*
		* @param {object} event - the event object of the 'brushing:undoRedo' event
		*/
		_onUndoRedo: function(payload) {
			payload = payload || {};
			if (payload.undoRedoTransactionType === 'undo') {
				payload.ignorePageContextChanged = payload.ignoreUndoPageContextChanged;
			} else if (payload.undoRedoTransactionType === 'redo') {
				payload.ignorePageContextChanged = payload.ignoreRedoPageContextChanged;
			}
		},

		_onRemoveFilterIndicatorEntry: function(payload) {
			payload = payload || {};
			var pageContextItem = payload.pageContext.getPageContextItem({ id: payload.filterToDelete });
			if (pageContextItem) {
				var synchDataId = pageContextItem.getSynchDataId();
				//@todo what is isValueDataItem?
				if(pageContextItem.getIsModeledFilter()){
					payload.pageContext.deletePageContextItems({ id: payload.filterToDelete });
				}else if (!synchDataId || pageContextItem.getOrigin() !== 'visualization' && !pageContextItem.isValueDataItem()) {
					if (pageContextItem.getOrigin() === 'visualization') {
						payload.pageContext.deletePageContextItems({ id: payload.filterToDelete, eventSourceId: pageContextItem.getEventSourceId() });
					} else {
						// defect288403: nameset filter with no selection, if remove from the filter indicator, the page context entry should be removed too
						if (pageContextItem.isValueDataItem()) {
							const isItemRemoved = payload.pageContext.deletePageContextItems({ id: payload.filterToDelete });
							if (isItemRemoved) {
								// clear the cache in filterDockItems to synch
								const currentScope =  this.filterDockItems[pageContextItem.getScope()] = this.filterDockItems[pageContextItem.getScope()] || {};
								const currentSource = currentScope[pageContextItem.getSourceId()] = currentScope[pageContextItem.getSourceId()] || {};
								delete currentSource[pageContextItem.getItemId()];
							}
						} else {
							payload.pageContext.clearPageContextItemValues({ id: payload.filterToDelete });
						}
					}
				} else if (synchDataId) {
					var deleteSpec = JSON.parse(payload.filterToDelete);
					var deleteOptions = {
						sourceIdsToUpdate: {},
						removedItems: [],
						payloadData: {
							undoRedoTransactionId: UniqueId.get('delete_viz_filter_entry_'),
							transactionToken: payload.transactionToken
						}
					};

					var netPageContext = this._getNetPageContextItems(deleteSpec.scope, deleteSpec.eventGroupId, 'visualization', synchDataId);

					netPageContext.forEach(pageContext => {
						payload.pageContext.deleteVisTypePageContext({ id: pageContext.getKey() }, deleteOptions);
					});

					if (!deleteOptions.handlers || !deleteOptions.handlers.filterContextUpdated) {
						throw new Error ('Invalid delete filter indicator options');
					}

					var updatateContext = {
						removedList: deleteOptions.removedItems,
						removedItems: deleteOptions.removedItems,
						sourceIdsToUpdate: deleteOptions.sourceIdsToUpdate
					};

					deleteOptions.handlers.filterContextUpdated(updatateContext , { transactionId: deleteOptions.transactionId });
				}
			}
		},

		/**
		* Returns a boolean indicating whether can process 'change:pageContext' event
		*
		* @param {object} event - the event object of the 'change:pageContext' event
		*
		* @return {boolean} true if can process else return false
		*/
		_canProcessPageContextChange: function(event) {
			event = event || {};
			var data = event.data || {};
			return data.isBrushed && event.name !== 'add';
		},

		/**
		* Callback to process render complete event for a vis widget
		*
		* @param {object} payload - the payloda to process the event
		*/
		_onRenderComplete: function(payload = {}) {
			let widget = payload.widget;
			let id = widget && widget.widgetAPI ? widget.widgetAPI.getId() : null;
			if (id) {
				this.widgets[id] = {
					getEdgeSelection: () => {
						return widget.getEdgeSelection();
					},
					getSourceId: () => {
						return widget.widgetAPI.getVisApi().getModule().getSourceId();
					},

					getId: () => {
						return widget.widgetAPI.getId();
					}
				};
			}
		},

		/**
		* Callback to process the remove vis widget event
		*
		* @param {object} payload - the payloda to process the event
		*/
		_onRemoveWidget: function(payload = {}) {
			let value = payload.value || {};
			let parameter = value.parameter || [];
			parameter.forEach(id => {
				delete this.widgets[id];
			});
		}
	});

	return SynchronizeDataModel;
});


/** Example:
[
  {
    "sourceId": "QC_Data",
	'tableRef: "Sheet_1",
    "synchronizeItems": [
      {
        "id": "Product line",
        "items": [
          {
            "sourceId": "Hollywood Movies",
			'tableRef: "Sheet_2",
            "id": "Product line"
          },
          {
            "sourceId": "Sales and Marketing",
            "id": "Product line"
          }
        ]
      },
      {
        "id": "Year",
        "items": [
          {
            "sourceId": "Hollywood Movies",
            "id": "Year"
          }
        ]
      }
    ]
  },
  {
    "sourceId": "Hollywood Movies",
    "synchronizeItems": [
      {
        "itemId": "Country",
        "items": [
          {
            "sourceId": "QC_Data",
            "itemId": "Country"
          },
          {
            "sourceId": "Sales and Marketing",
            "itemId": "Country"
          }
        ]
      },
      {
        "id": "Location",
        "items": [
          {
            "sourceId": "Hollywood Movies",
            "itemId": "Location"
          }
        ]
      }
    ]
  }
]
*/
